/**
 * SpotlightOverlay Component
 *
 * Creates a guided onboarding experience by highlighting specific UI elements
 * with a spotlight effect and showing contextual tooltips.
 */

import { useState, useEffect, useCallback, type CSSProperties } from 'react';

export interface SpotlightStep {
  /** Unique identifier for the step */
  id: string;
  /** CSS selector or element ID to highlight (without #) */
  targetSelector: string;
  /** Title text for the tooltip */
  title: string;
  /** Description text for the tooltip */
  description: string;
  /** Position of tooltip relative to target */
  tooltipPosition?: 'top' | 'bottom' | 'left' | 'right';
  /** Padding around the spotlight cutout */
  padding?: number;
  /** Optional callback when this step is shown */
  onShow?: () => void;
  /** Hide the Next/Done button - forces user to interact with the actual element */
  hideActionButton?: boolean;
  /** Custom label for the skip button (default: "Skip") */
  skipButtonLabel?: string;
}

export interface SpotlightOverlayProps {
  /** Array of steps to guide user through */
  steps: SpotlightStep[];
  /** Current step index */
  currentStep: number;
  /** Called when user advances to next step */
  onNext: () => void;
  /** Called when user skips onboarding */
  onSkip: () => void;
  /** Called when onboarding is complete (after last step) */
  onComplete: () => void;
  /** Whether the overlay is visible */
  isVisible: boolean;
}

interface TargetRect {
  top: number;
  left: number;
  width: number;
  height: number;
}

export const SpotlightOverlay = ({
  steps,
  currentStep,
  onNext,
  onSkip,
  onComplete,
  isVisible,
}: SpotlightOverlayProps) => {
  const [targetRect, setTargetRect] = useState<TargetRect | null>(null);
  const [isAnimating, setIsAnimating] = useState(false);

  const step = steps[currentStep];
  const isLastStep = currentStep === steps.length - 1;

  // Find and measure the target element
  const measureTarget = useCallback(() => {
    if (!step) return;

    // Try ID first, then querySelector
    let element = document.getElementById(step.targetSelector);
    if (!element) {
      element = document.querySelector(step.targetSelector) as HTMLElement;
    }

    if (element) {
      const rect = element.getBoundingClientRect();
      const padding = step.padding ?? 8;
      setTargetRect({
        top: rect.top - padding,
        left: rect.left - padding,
        width: rect.width + padding * 2,
        height: rect.height + padding * 2,
      });

      // Scroll element into view if needed
      if (rect.top < 0 || rect.bottom > window.innerHeight) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        // Re-measure after scroll
        setTimeout(() => {
          const newRect = element!.getBoundingClientRect();
          setTargetRect({
            top: newRect.top - padding,
            left: newRect.left - padding,
            width: newRect.width + padding * 2,
            height: newRect.height + padding * 2,
          });
        }, 300);
      }
    } else {
      // Target not found - show centered tooltip
      setTargetRect(null);
    }
  }, [step]);

  // Measure on step change and window resize
  useEffect(() => {
    if (!isVisible || !step) return;

    setIsAnimating(true);
    measureTarget();
    step.onShow?.();

    const timer = setTimeout(() => setIsAnimating(false), 300);

    const handleResize = () => measureTarget();
    window.addEventListener('resize', handleResize);

    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', handleResize);
    };
  }, [isVisible, step, measureTarget]);

  // Handle next button
  const handleNext = useCallback(() => {
    if (isLastStep) {
      onComplete();
    } else {
      onNext();
    }
  }, [isLastStep, onNext, onComplete]);

  if (!isVisible || !step) return null;

  // Calculate tooltip position
  const tooltipPosition = step.tooltipPosition ?? 'bottom';
  const tooltipStyle = getTooltipStyle(targetRect, tooltipPosition);

  return (
    <div style={overlayContainerStyle}>
      {/* Semi-transparent overlay with spotlight cutout */}
      <svg style={svgStyle} viewBox={`0 0 ${window.innerWidth} ${window.innerHeight}`}>
        <defs>
          <mask id="spotlight-mask">
            {/* White = visible, black = hidden */}
            <rect x="0" y="0" width="100%" height="100%" fill="white" />
            {targetRect && (
              <rect
                x={targetRect.left}
                y={targetRect.top}
                width={targetRect.width}
                height={targetRect.height}
                rx="8"
                fill="black"
                style={{
                  transition: isAnimating ? 'all 300ms ease-out' : 'none',
                }}
              />
            )}
          </mask>
        </defs>
        <rect
          x="0"
          y="0"
          width="100%"
          height="100%"
          fill="rgba(0, 0, 0, 0.7)"
          mask="url(#spotlight-mask)"
        />
      </svg>

      {/* Highlight border around target */}
      {targetRect && (
        <div
          style={{
            ...highlightBorderStyle,
            top: targetRect.top,
            left: targetRect.left,
            width: targetRect.width,
            height: targetRect.height,
            transition: isAnimating ? 'all 300ms ease-out' : 'none',
          }}
        />
      )}

      {/* Tooltip */}
      <div
        style={{
          ...tooltipContainerStyle,
          ...tooltipStyle,
          opacity: isAnimating ? 0 : 1,
          transform: isAnimating ? 'translateY(10px)' : 'translateY(0)',
        }}
      >
        <div style={tooltipContentStyle}>
          <h3 style={tooltipTitleStyle}>{step.title}</h3>
          <p style={tooltipDescriptionStyle}>{step.description}</p>
        </div>

        <div style={tooltipActionsStyle}>
          <div style={stepIndicatorStyle}>
            {currentStep + 1} of {steps.length}
          </div>
          <div style={buttonGroupStyle}>
            <button onClick={onSkip} style={skipButtonStyle} type="button">
              {step.skipButtonLabel ?? 'Skip'}
            </button>
            {!step.hideActionButton && (
              <button onClick={handleNext} style={nextButtonStyle} type="button">
                {isLastStep ? 'Done' : 'Next'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Calculate tooltip position based on target rect and desired position
function getTooltipStyle(
  targetRect: TargetRect | null,
  position: 'top' | 'bottom' | 'left' | 'right'
): CSSProperties {
  const tooltipWidth = 300;
  const tooltipGap = 16;

  if (!targetRect) {
    // Center on screen if no target, but keep within viewport
    // Use explicit pixel values to ensure tooltip is fully visible on mobile
    // Account for bottom nav (~60px) and estimate tooltip height (~180px)
    const tooltipHeight = 180;
    const bottomNavHeight = 80; // Extra buffer for bottom nav + safe area
    const availableHeight = window.innerHeight - bottomNavHeight;
    const topPosition = Math.max(16, (availableHeight - tooltipHeight) / 2);

    return {
      top: topPosition,
      left: (window.innerWidth - tooltipWidth) / 2,
    };
  }

  const targetCenterX = targetRect.left + targetRect.width / 2;
  const targetCenterY = targetRect.top + targetRect.height / 2;

  // Keep tooltip within viewport bounds
  const clampX = (x: number) => Math.max(16, Math.min(x, window.innerWidth - tooltipWidth - 16));
  const clampY = (y: number) => Math.max(16, Math.min(y, window.innerHeight - 200)); // ~200 for tooltip height + actions

  switch (position) {
    case 'top':
      return {
        bottom: window.innerHeight - targetRect.top + tooltipGap,
        left: clampX(targetCenterX - tooltipWidth / 2),
      };
    case 'bottom': {
      const tooltipHeight = 180; // Estimated tooltip height
      const bottomNavHeight = 80; // Space for bottom nav
      const maxTop = window.innerHeight - tooltipHeight - bottomNavHeight;
      const desiredTop = targetRect.top + targetRect.height + tooltipGap;
      return {
        top: Math.min(desiredTop, maxTop),
        left: clampX(targetCenterX - tooltipWidth / 2),
      };
    }
    case 'left': {
      // Calculate left position with clamping
      const rightEdge = window.innerWidth - targetRect.left + tooltipGap;
      // If tooltip would be off-screen left, fall back to centered bottom position
      if (rightEdge > window.innerWidth - 16) {
        return {
          top: targetRect.top + targetRect.height + tooltipGap,
          left: clampX(targetCenterX - tooltipWidth / 2),
        };
      }
      return {
        top: clampY(targetCenterY - 50),
        right: rightEdge,
      };
    }
    case 'right': {
      // Calculate right position with clamping
      const leftEdge = targetRect.left + targetRect.width + tooltipGap;
      // If tooltip would be off-screen right, fall back to centered bottom position
      if (leftEdge + tooltipWidth > window.innerWidth - 16) {
        return {
          top: targetRect.top + targetRect.height + tooltipGap,
          left: clampX(targetCenterX - tooltipWidth / 2),
        };
      }
      return {
        top: clampY(targetCenterY - 50),
        left: leftEdge,
      };
    }
    default:
      return {
        top: targetRect.top + targetRect.height + tooltipGap,
        left: clampX(targetCenterX - tooltipWidth / 2),
      };
  }
}

// ==================== STYLES ====================

const overlayContainerStyle: CSSProperties = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  zIndex: 9999,
  pointerEvents: 'none', // Allow clicks through to spotlighted elements
};

const svgStyle: CSSProperties = {
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  pointerEvents: 'none',
};

const highlightBorderStyle: CSSProperties = {
  position: 'absolute',
  borderRadius: 8,
  border: '2px solid var(--color-primary, #3b82f6)',
  boxShadow: '0 0 0 4px rgba(59, 130, 246, 0.3)',
  pointerEvents: 'none',
};

const tooltipContainerStyle: CSSProperties = {
  position: 'absolute',
  width: 300,
  backgroundColor: 'var(--color-bg-primary, white)',
  borderRadius: 12,
  boxShadow: '0 10px 40px rgba(0, 0, 0, 0.3)',
  overflow: 'hidden',
  transition: 'opacity 200ms ease-out, transform 200ms ease-out',
  pointerEvents: 'auto', // Enable clicks on tooltip buttons
};

const tooltipContentStyle: CSSProperties = {
  padding: '20px 20px 16px',
};

const tooltipTitleStyle: CSSProperties = {
  margin: 0,
  marginBottom: 8,
  fontSize: '1.125rem',
  fontWeight: 600,
  color: 'var(--color-text-primary, #1f2937)',
};

const tooltipDescriptionStyle: CSSProperties = {
  margin: 0,
  fontSize: '0.9375rem',
  color: 'var(--color-text-secondary, #6b7280)',
  lineHeight: 1.5,
};

const tooltipActionsStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '12px 20px',
  backgroundColor: 'var(--color-bg-secondary, #f9fafb)',
  borderTop: '1px solid var(--color-border, #e5e7eb)',
};

const stepIndicatorStyle: CSSProperties = {
  fontSize: '0.8125rem',
  color: 'var(--color-text-secondary, #9ca3af)',
};

const buttonGroupStyle: CSSProperties = {
  display: 'flex',
  gap: 8,
};

const skipButtonStyle: CSSProperties = {
  padding: '8px 12px',
  fontSize: '0.875rem',
  fontWeight: 500,
  color: 'var(--color-text-secondary, #6b7280)',
  backgroundColor: 'transparent',
  border: 'none',
  borderRadius: 6,
  cursor: 'pointer',
};

const nextButtonStyle: CSSProperties = {
  padding: '8px 16px',
  fontSize: '0.875rem',
  fontWeight: 600,
  color: 'white',
  backgroundColor: 'var(--color-primary, #3b82f6)',
  border: 'none',
  borderRadius: 6,
  cursor: 'pointer',
};

export default SpotlightOverlay;
